import React from 'react';
import strings from '../../../strings/strings.json';
import { AuthContext } from 'contextProviders/AuthContext';
import useGetRequest from 'hooks/useGetRequest';
import Button from 'components/buttons/Button';
import { FileDocument } from 'models/FileDocument';
import SelectFileForm from './SelectFileForm';
import { FileMetaData, FileType } from 'components/fileUpload/strategies/strategies';
import Modal from 'components/modals/Modal';
import AddIcon from 'assets/icons/controls/AddIcon';

type FileSelectionComponentProps = {
    setFileFromLocal: (newFile: File | null | undefined) => void;
    setFileFromProvider: (video: FileDocument) => void;
    setFileFromLocalMetaData: (newMetaData: FileMetaData | undefined) => void;
    disableButton: boolean;
    selectFileButtonRef: React.RefObject<HTMLButtonElement>;
};

export function VideoSelectionComponent({
    setFileFromLocal,
    setFileFromProvider,
    setFileFromLocalMetaData,
    disableButton,
    selectFileButtonRef,
}: FileSelectionComponentProps) {
    const authContext = React.useContext(AuthContext);
    const userOrganisation = authContext.userData.organisation;
    const { data: videos, loading: loadingVideos } = useGetRequest<FileDocument[]>(
        `organisations/${userOrganisation?.id}/documents?document_type=VIDEO`,
    );

    return (
        <FileSelectionComponent
            buttonTitle={strings.selectFileForm.selectVideoButton}
            buttonAria={strings.selectFileForm.selectVideoButtonAria}
            setFileFromLocal={setFileFromLocal}
            setFileFromProvider={setFileFromProvider}
            setFileFromLocalMetaData={setFileFromLocalMetaData}
            disableButton={disableButton}
            organisationName={userOrganisation?.name ?? ''}
            filesFromProvider={videos}
            loadingFiles={loadingVideos}
            chosenFileType={FileType.VIDEO}
            selectFileButtonRef={selectFileButtonRef}
        />
    );
}

export function ImageSelectionComponent({
    setFileFromLocal,
    setFileFromProvider,
    setFileFromLocalMetaData,
    disableButton,
    selectFileButtonRef,
}: FileSelectionComponentProps) {
    const authContext = React.useContext(AuthContext);
    const userOrganisation = authContext.userData.organisation;
    const { data: images, loading: loadingImages } = useGetRequest<FileDocument[]>(
        `organisations/${userOrganisation?.id}/documents?document_type=IMAGE`,
    );

    return (
        <FileSelectionComponent
            buttonTitle={strings.selectFileForm.selectImageButton}
            buttonAria={strings.selectFileForm.selectImageButtonAria}
            setFileFromLocal={setFileFromLocal}
            setFileFromProvider={setFileFromProvider}
            disableButton={disableButton}
            organisationName={userOrganisation?.name ?? ''}
            filesFromProvider={images}
            loadingFiles={loadingImages}
            setFileFromLocalMetaData={setFileFromLocalMetaData}
            chosenFileType={FileType.IMAGE}
            selectFileButtonRef={selectFileButtonRef}
        />
    );
}

export function AudioSelectionComponent({
    setFileFromLocal,
    setFileFromProvider,
    setFileFromLocalMetaData,
    disableButton,
    selectFileButtonRef,
}: FileSelectionComponentProps) {
    const authContext = React.useContext(AuthContext);
    const userOrganisation = authContext.userData.organisation;
    const { data: audio, loading: loadingAudio } = useGetRequest<FileDocument[]>(
        `organisations/${userOrganisation?.id}/documents?document_type=AUDIO`,
    );

    return (
        <FileSelectionComponent
            buttonTitle={strings.selectFileForm.audio.selectAudioButton}
            buttonAria={strings.selectFileForm.audio.selectAudioButtonAria}
            setFileFromLocal={setFileFromLocal}
            setFileFromProvider={setFileFromProvider}
            disableButton={disableButton}
            organisationName={userOrganisation?.name ?? ''}
            filesFromProvider={audio}
            loadingFiles={loadingAudio}
            setFileFromLocalMetaData={setFileFromLocalMetaData}
            chosenFileType={FileType.AUDIO}
            selectFileButtonRef={selectFileButtonRef}
        />
    );
}

type InnerFileSelectionComponentProps = {
    buttonTitle: string;
    buttonAria: string;
    setFileFromLocal: (newFile: File | null | undefined) => void;
    setFileFromProvider: (video: FileDocument) => void;
    setFileFromLocalMetaData: (newMetaData: FileMetaData | undefined) => void;
    disableButton: boolean;
    organisationName: string;
    filesFromProvider: FileDocument[] | null;
    loadingFiles: boolean;
    chosenFileType: FileType;
    selectFileButtonRef: React.RefObject<HTMLButtonElement>;
};

function FileSelectionComponent({
    buttonTitle,
    buttonAria,
    setFileFromLocal,
    setFileFromProvider,
    setFileFromLocalMetaData,
    disableButton,
    organisationName,
    filesFromProvider,
    loadingFiles,
    chosenFileType,
    selectFileButtonRef,
}: InnerFileSelectionComponentProps) {
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);

    return (
        <>
            <Button
                ref={selectFileButtonRef}
                title={buttonTitle}
                aria={buttonAria}
                onClick={() => setModalOpen(true)}
                disabled={disableButton}
                fullwidth
                icon={<AddIcon />}
            />
            {modalOpen && (
                <Modal
                    headlineText={strings.selectFileForm.title}
                    onClickCloseButton={() => setModalOpen(false)}
                    modalBody={
                        <SelectFileForm
                            organisationName={organisationName}
                            filesFromProvider={filesFromProvider ?? []}
                            loadingFiles={loadingFiles}
                            setFileFromLocal={setFileFromLocal}
                            setFileFromProvider={setFileFromProvider}
                            setFileFromLocalMetaData={setFileFromLocalMetaData}
                            closeModal={() => {
                                setModalOpen(false);
                                selectFileButtonRef.current?.focus();
                            }}
                            chosenFileType={chosenFileType}
                        />
                    }
                />
            )}
        </>
    );
}

type FileSelectionModalProps = {
    headlineText: string;
    onClickCloseButton: () => void;
    organisationName: string;
    filesFromProvider: FileDocument[];
    loadingFiles: boolean;
    setFileFromLocal: (newFile: File) => void;
    setFileFromProvider: () => void;
    setFileFromLocalMetaData: (newFileMetaData: FileMetaData | undefined) => void;
    chosenFileType: FileType;
};

export function FileSelectionModal({
    headlineText,
    onClickCloseButton,
    organisationName,
    filesFromProvider,
    loadingFiles,
    setFileFromLocal,
    setFileFromProvider,
    setFileFromLocalMetaData,
    chosenFileType,
}: FileSelectionModalProps) {
    return (
        <Modal
            headlineText={headlineText}
            onClickCloseButton={onClickCloseButton}
            modalBody={
                <SelectFileForm
                    organisationName={organisationName}
                    filesFromProvider={filesFromProvider ?? []}
                    loadingFiles={loadingFiles}
                    setFileFromLocal={setFileFromLocal}
                    setFileFromProvider={setFileFromProvider}
                    setFileFromLocalMetaData={setFileFromLocalMetaData}
                    closeModal={() => {
                        onClickCloseButton();
                        // selectFileButtonRef.current?.focus();
                    }}
                    chosenFileType={chosenFileType}
                />
            }
        />
    );
}
